import { KeysMatching, ObjectWithID, ObjectWithStringID } from '../shared/models/type-helper';
import { RequiredNonNullable, getIsNotNullable, getIsNullable } from './nullable-utils';
import { CompareIds, getIsFiniteNumber } from './primitive-utils';

export type InterfaceOf<T> = { [K in keyof T]: T[K] };

export function getHasValidId<T extends ObjectWithID>(value: T): value is RequiredNonNullable<T> {
  return getIsFiniteNumber(value?.id);
}

export const CompareById =
  <T extends ObjectWithID>(first?: T) =>
  <P extends ObjectWithID>(second?: P) =>
    CompareIds(first?.id, second?.id);

export function toObject(previousValue: {}, currentValue: {}): {} {
  return { ...previousValue, ...currentValue };
}

interface ObjectWithChildren {
  children?: ObjectWithChildren[];
}
export function mapRecursively<T extends ObjectWithChildren>(items: T[], mapFn: (item: T) => T): T[] {
  return items.map(item => {
    const children = getIsNotNullable(item.children)
      ? mapRecursively(item.children as T[], mapFn)
      : undefined;
    item.children = children as unknown as ObjectWithChildren[] | undefined;

    return mapFn(item);
  });
}

export function filterRecursively<T extends ObjectWithChildren>(
  items: T[],
  filterFn: (item: T) => boolean,
): T[] {
  return items.filter(item => {
    const children = getIsNotNullable(item.children)
      ? filterRecursively(item.children as T[], filterFn)
      : undefined;
    item.children = children as unknown as ObjectWithChildren[] | undefined;

    return filterFn(item);
  });
}

/**
 * Stringifies object IDs that are numbers.
 * Note that anything that is not a number or string will be returned as is.
 * This is considered okay because the function signature types what the input
 * should be, and as long as those types hold TS will inform us of any misuse.
 */
export function toObjectWithStringID<T extends ObjectWithID>(obj: T): T & Partial<ObjectWithStringID> {
  return getIsFiniteNumber(obj.id) ? { ...obj, id: `${obj.id}` } : (obj as T & Partial<ObjectWithStringID>);
}

/**
 * Turns number keys into strings. Useful to map number ids to strings in a typesafe manner.
 * Works well in combination with getIsNotNullableProps().
 *
 * @param obj the input object
 * @param keys the keys of the object that have number values
 * @returns the object with the keyed values turned to strings
 */
export function stringifyNumberProps<T, K extends KeysMatching<T, string | number>>(
  obj: NonNullable<T>,
  ...keys: [K, ...K[]]
): T & { [P in K]: string } {
  return {
    ...obj,
    ...(Object.fromEntries(keys.map(key => [key, getIsNotNullable(obj[key]) ? `${obj[key]}` : obj[key]])) as {
      [P in K]: string;
    }),
  };
}

/**
 * Performs a shallow patch of the given objects with corresponding patches applied.
 * Objects and patches are matched by id.
 *
 * @param param0 describes the patches, the objects, and the matching key used as an id
 * @returns the patched object
 */
export function patchObjectsShallow<T, K extends keyof T>({
  objects,
  patches,
  id,
}: {
  objects: T[];
  patches: Required<Pick<T, K>>[];
  id: K;
}): T[] {
  const map = new Map(objects.map(obj => [obj[id], obj]));
  patches.forEach(patch => {
    const current = map.get(patch[id]);
    if (getIsNullable(current)) {
      console.error('Failed to apply patch; id not found', patch);
      return;
    }

    map.set(patch[id], { ...current, ...patch });
  });

  return Array.from(map, ([, value]) => value);
}
